import { useMutation } from '@tanstack/react-query';

import { GeodataBoundaryPolygonModel } from '../../../../../../../../../typings/api/skymap/rest/v1/.common';
import { PatchDroneHarmonyFlightPlanParameters } from '../../../../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../../../../../utils/typescript-utils';
import { useErrorHandling } from '../../../../../../hooks/use-error-handling';
import { GcpRow } from '../../../../../cloud-processing/gcp-parser';
import { FormValues } from './index';

type Props = {
  projectId: string;
  flightPlanId: string;
  gcpFileContent: GcpRow[];
  boundaryPolygonUploadIds: string[];
  existingBoundaryPolygons: GeodataBoundaryPolygonModel[];
  onSuccess: () => void;
};

const useUpdateQuery = (props: Props) => {
  const {
    clearErrors: clearApiErrors,
    getErrorForPath,
    handleError,
    buildErrorList,
    hasError,
    excludeErrorPaths,
  } = useErrorHandling<RequestParamsToApiPaths<PatchDroneHarmonyFlightPlanParameters>>();

  const updateQuery = useMutation({
    mutationFn: (form: FormValues) =>
      SkyMapAxiosServiceFactory.instance.createProjectServiceV1().patchDroneHarmonyFlightPlan({
        body: {
          package:
            form.geodataProcessType === 'ORTHO_ONLY'
              ? {
                  geodataProcessType: 'ORTHO_ONLY',
                }
              : {
                  generateTerrainModel: form.generateTerrainModel,
                  geodataProcessType: form.geodataProcessType,
                },
          groundControlPoints:
            props.gcpFileContent.length > 0
              ? props.gcpFileContent.map((x) => ({
                  easting: parseFloat(x.e.value),
                  height: parseFloat(x.h.value),
                  name: x.name.value,
                  northing: parseFloat(x.n.value),
                }))
              : null,
          boundaryPolygonUploadIds:
            props.boundaryPolygonUploadIds.length > 0 ? props.boundaryPolygonUploadIds : undefined,
          boundaryPolygons:
            props.existingBoundaryPolygons.length > 0
              ? props.existingBoundaryPolygons.map((bp) => ({
                  id: bp.id,
                  enabled: bp.enabled,
                  boundaryType: bp.boundaryType,
                }))
              : undefined,
        },
        path: {
          projectId: props.projectId,
          flightPlanId: props.flightPlanId,
        },
      }),
    retry: false,
    networkMode: 'always',
    onSuccess: () => props.onSuccess(),
    onMutate: () => clearApiErrors(),
    onError: (err) => handleError(err),
  });

  const errorList =
    hasError(excludeErrorPaths(['body.package', 'body.groundControlPoints'])) &&
    buildErrorList({
      filter: excludeErrorPaths(['body.package', 'body.groundControlPoints']),
    });

  return {
    updateQuery,
    getErrorForPath,
    errorList,
  };
};

export { useUpdateQuery };
